import React, { useMemo } from "react";
import ScoreBadge from "./ScoreBadge";
import { useAuthStore } from "../../store/authStore";
import { CheckCircle, XCircle } from "lucide-react";
import SummaryCell from "./LeadSummaryCell";
import LeadCategory from "./LeadCategory";
import SourceBadges from "../common/badge/SourceBadges";
import IntentBadge from "../common/badge/IntentBadge";

export const useColumns = () => {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";

  const columns = useMemo(() => {
    const propertyColumn = {
      title: "Property",
      dataIndex: "property_name",
      render: (text) => <span className="badge badge-property">{text}</span>,
    };

    const crmPushedColumn = {
      title: "CRM Pushed",
      dataIndex: "crm_pushed",
      align: "center",
      render: (value) => (
        <span className="d-flex align-center justify-center">
          {value ? (
            <CheckCircle size={16} className="text-success" />
          ) : (
            <XCircle size={16} className="text-danger" />
          )}
        </span>
      ),
    };

    const otherColumns = [
      {
        title: "Name",
        dataIndex: "name",
        render: (text) => <span className="fw-medium">{text}</span>,
      },
      {
        title: "Phone",
        key: "phone",
        dataIndex: "phone",
      },
      {
        title: "Score",
        key: "score",
        dataIndex: "score",
        render: (score) => <ScoreBadge score={score} />,
      },
      {
        title: "Category",
        key: "category",
        dataIndex: "score",
        render: (score) => <LeadCategory score={score} />,
      },
      {
        title: "Intent",
        key: "intent",
        dataIndex: "intent",
        render: (intent) => <IntentBadge intent={intent} />,
      },
      {
        title: "Location",
        key: "location",
        dataIndex: "location",
        render: (text) => <span className="badge badge-location">{text}</span>,
      },
      {
        title: "Budget",
        key: "budget",
        dataIndex: "budget",
        render: (text) => (
          <span className="fw-medium text-primary text-nowrap">
            {text || "—"}
          </span>
        ),
      },
      {
        title: "Currency",
        key: "budget_currency",
        dataIndex: "budget_currency",
        render: (text) => (
          <span className="fw-medium text-nowrap">{text || "—"}</span>
        ),
      },
      {
        title: "Unit Type",
        key: "unit_type",
        dataIndex: "unit_type",
        render: (text) => <span className="badge badge-unit-type">{text}</span>,
      },
      {
         title: "Source",
         key: "source",
         dataIndex: "sources",
         render: (sources, row) => <SourceBadges sources={sources} isAdLead={row.is_ad_lead} />,
       },
      {
        title: "Conversation Summary",
        key: "summary",
        dataIndex: "summary",
        className: "summary-col",
        render: (text) => <SummaryCell text={text} />,
      },
      {
        title: "Activity Summary",
        key: "activity_summary",
        dataIndex: "activity_summary",
        render: (text) =>
          text && text.trim() ? (
            <SummaryCell text={text} title="Activity Summary" />
          ) : (
            <span className="fw-medium text-primary">No activity summary</span>
          ),
      },
    ];

    let baseColumns = [];

    if (isAdmin) {
      baseColumns = [
        {
          title: "Agent",
          key: "agent_name",
          dataIndex: "agent_name",
          render: (text) => (
            <span className="fw-medium text-primary">{text}</span>
          ),
        },
        propertyColumn,
        ...otherColumns,
        crmPushedColumn,
      ];
    } else {
      // Agent/User view → Property first
      baseColumns = [propertyColumn, ...otherColumns];
    }

    return baseColumns;
  }, [isAdmin]);

  return columns;
};
